<template>
<div>
    <Navbar/>
    <section class="fct-hero-im" id="old-carousel">
      <div class="hero-bg-wrap">
      <img src="@/assets/banner/background.png" alt="" class="hero-bg-img">
        <div class="hero-mask"></div>
      </div>
      <div class="container">
        <div class="content-block">
          <h1 class="hero-title">Your Quality, Our Priority</h1>
          <div class="desc">
            <p>ISO Standards</p>
          </div>
           <ul class="feature-list">
            <li class="feature-item">
              <span class="check-icon">✓</span>
              Rigorous inspections using advanced metrology equipment
            </li>
            <li class="feature-item">
              <span class="check-icon">✓</span>
              Traceable and documented process control
            </li>
            <li class="feature-item">
              <span class="check-icon">✓</span>
              Complete inspection reports and certificates
            </li>
          </ul>
          <div class="btn-group">
            <span class="btn btn-cnc-quote" @click="$router.push('/quote')">Get Instant Quote</span>
            <p class="text-small">All uploads files are safe and confidential</p>
          </div>
        </div>
      </div>
    </section>

    <div class="qa-container">
    <section class="intro-section">
      <h1 class="section-title">Quality Assurance at XCL</h1>
      <p class="intro-text">
        At XCL, quality assurance is implemented in every stage of our process. From material sourcing to final delivery, we adhere to rigorous standards to ensure your parts meet or exceed expectations. We are certified under ISO 9001 (Quality Management), ISO 13485 (Medical Devices), and ISO 14001 (Environmental Management), and maintain full traceability throughout our operations.
      </p>
    </section>

    <section class="grid-2-col">
      <div class="list-column">
        <h2 class="column-title">Tests & Capabilities</h2>
        <ul class="bullet-list">
          <li><strong>Dimensional Inspection:</strong> up to ±0.01 mm for precision machining; verified via <strong>CMM</strong> and vision systems.</li>
          <li><strong>Hardness / Tensile / Elongation / Yield Strength Tests:</strong> with certified machines.</li>
          <li><strong>Appearance / Finish:</strong> color matching, visual inspection under specified lighting, surface roughness (Ra values) measurement.</li>
          <li><strong>Special Tests</strong> (upon request): e.g. non-destructive testing (NDT), coating adhesion, corrosion resistance, etc.</li>
        </ul>
      </div>
      <div class="list-column">
        <h2 class="column-title">Tools & Equipment Used:</h2>
        <ul class="bullet-list">
          <li><strong>Digital calipers and micrometers:</strong> ±0.01 mm</li>
          <li><strong>Hardness testers:</strong> Rockwell / Brinell / Shore Hardness Tester</li>
          <li><strong>Coordinate Measuring Machine (CMM):</strong> ±2 μm accuracy</li>
          <li><strong>2.5D Vision Measuring System</strong> for small and complex parts</li>
          <li><strong>Surface Roughness Tester</strong> (0.1–0.8 μm Ra typical)</li>
          <li><strong>Height Gauge, Pin Gauges</strong>, and <strong>Thread Gauges</strong></li>
        </ul>
      </div>
    </section>

    <section class="feature-block">
      <div class="image-wrapper">
        <img :src="materialImages[materialIndex]" alt="Material Testing" />
        <div class="carousel-dots">
          <span 
            v-for="(img, index) in materialImages" 
            :key="index"
            class="dot" 
            :class="{ active: materialIndex === index }"
            @click="setMaterialIndex(index)"
          ></span>
        </div>
      </div>
      <div class="text-content">
        <h2 class="block-title">Material Qualification and Incoming Inspection</h2>
        <p class="block-desc">
          All raw materials are obtained from carefully selected suppliers known for their high quality. All materials and compounds will be go through extensive quality inspection before being stored or processed.
        </p>
        <ul class="arrow-list">
          <li>Appearance Check</li>
          <li>Dimension Check</li>
          <li>Tensile Strength Test</li>
          <li>Shore Hardness Test</li>
        </ul>
      </div>
    </section>

    <section class="feature-block reverse">
      <div class="text-content">
        <h2 class="block-title">First Article Inspection Report</h2>
        <p class="block-desc">
          First article inspection (FAI) is a design verification and design history file and a formal method of providing a reported measurement for each manufactured feature of a part or the final product.
        </p>
        <p class="block-desc">FAI reports include:</p>
        <ul class="bullet-list">
          <li>Verification of all critical dimensions (CMM or optical measurement)</li>
          <li>Comparison against 2D drawings and 3D CAD models</li>
          <li>Surface roughness (Ra) measurement</li>
          <li>Review of tolerances and geometry consistency</li>
          <li>Photo documentation and operator sign-off</li>
        </ul>
      </div>
      <div class="image-wrapper">
        <img src="@/assets/images/about_us/6.png" alt="First Article Inspection" />
      </div>
    </section>

    <section class="feature-block">
      <div class="image-wrapper">
        <img src="@/assets/images/about_us/7.png" alt="In-Process Quality Control" />
      </div>
      <div class="text-content">
        <h2 class="block-title">In-Process Quality Control (IPQC)</h2>
        <p class="block-desc">
          Every one of our manufacturing orders will go through the first article inspection when the machine is set up. FAI will be performed and recorded by IPQC and a senior engineer. The operator and IPQC inspection focuses on critical dimension checks at every stage of production.
        </p>
      </div>
    </section>

    <section class="feature-block reverse">
      <div class="text-content">
        <h2 class="block-title">Final Inspection and Pre-Shipment Verification</h2>
        <p class="block-desc">
          Before dispatch, every order passes through a comprehensive <strong>Pre-Shipment Inspection (PSI)</strong> to ensure conformity with specifications and appearance standards.
        </p>
        <p class="block-desc"><strong>Inspection scope:</strong></p>
        <ul class="bullet-list">
          <li>100% dimensional verification on critical features</li>
          <li>Surface finish and cosmetic inspection (scratches, burrs, tool marks, color)</li>
          <li>Functional checks and test assembly if applicable</li>
          <li>Packaging and labeling verification</li>
        </ul>
      </div>
      <div class="image-wrapper">
        <img :src="finalImages[finalIndex]" alt="Final Inspection" />
        <div class="carousel-dots">
          <span 
            v-for="(img, index) in finalImages" 
            :key="index"
            class="dot" 
            :class="{ active: finalIndex === index }"
            @click="setFinalIndex(index)"
          ></span>
        </div>
      </div>
    </section>
  </div>
  <quote/>
  <Footer/>
</div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Footer from './components/Footer.vue';
import Navbar from './components/Nav.vue';
import quote from './components/quote.vue';


// --- Material Testing Carousel ---
const materialImages = ref([
  require('@/assets/images/about_us/2.png'),
  require('@/assets/images/about_us/3.png'),
  require('@/assets/images/about_us/4.png'),
  require('@/assets/images/about_us/5.png')
]);
const materialIndex = ref(0);
let materialInterval = null;

const startMaterialCarousel = () => {
  materialInterval = setInterval(() => {
    materialIndex.value = (materialIndex.value + 1) % materialImages.value.length;
  }, 3500); 
};

const setMaterialIndex = (index) => {
  materialIndex.value = index;
  clearInterval(materialInterval);
  startMaterialCarousel();
};

// --- Final Inspection Carousel ---
const finalImages = ref([
   require('@/assets/images/about_us/8.png'),
    require('@/assets/images/about_us/9.png'),
     require('@/assets/images/about_us/10.png'),
      require('@/assets/images/about_us/11.png'),
       require('@/assets/images/about_us/12.png'),
]);
const finalIndex = ref(0);
let finalInterval = null;

const startFinalCarousel = () => {
  finalInterval = setInterval(() => {
    finalIndex.value = (finalIndex.value + 1) % finalImages.value.length;
  }, 3500);
};

const setFinalIndex = (index) => {
  finalIndex.value = index;
  clearInterval(finalInterval);
  startFinalCarousel();
};

// --- Lifecycle Hooks ---
onMounted(() => {
  startMaterialCarousel();
  startFinalCarousel();
});

onUnmounted(() => {
  if (materialInterval) clearInterval(materialInterval);
  if (finalInterval) clearInterval(finalInterval);
});
</script>

<style scoped>
/* 移除了之前添加的 .fade 相关过渡样式 */

.fct-hero-im {
  position: relative;
  width: 100%;
  min-height: 525px;
  padding: 60px 0;
  overflow: hidden;
  background: #ffffff;
  margin-top: -60px;
}

.hero-bg-wrap {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.fct-hero-im .container {
  padding: 0 608px 0 0; 
  max-width: 1200px;
  margin: 0 auto;
  position: relative; 
  z-index: 10;      
}

.content-block {
  width: 80%;
  margin: 0 auto;
  text-align: left;
}

.hero-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.9;
  display: block;
}

.hero-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.95) 40%, rgba(255, 255, 255, 0) 80%);
  z-index: 2;
}

.hero-title {
  font-size: 42px;
  font-weight: 600;
  color: #333333;
  line-height: 1.15;
  margin-bottom: 1.5rem;
  font-family: inherit;
  white-space: nowrap; 
  overflow: visible;
}

.desc p {
  font-size: 1rem;
  color: #555555;
  line-height: 1.7;
  margin-bottom: 12px;
  font-family: inherit;
  font-weight: 500;
}

.feature-list {
  list-style: none;
  margin: 0 0 30px 0;
  padding: 0;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  font-size: 16px;
  color: #505050;
  margin-bottom: 4px;
  line-height: 24px; 
  font-weight: 500;
}

.check-icon {
  color: #16bc9c;
  font-weight: bold;
  margin-right: 12px;
  font-size: 20px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.btn-group {
  margin: 30px 0;
}

.btn-cnc-quote {
  display: inline-block;
  background-color: #16bc9c;
  color: #ffffff;
  font-size: 1rem;
  padding: 14px 29px;
  border-radius: 4px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: color 0.4s;
}

.btn-cnc-quote::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: #027062;
  transition: all 0.4s;
  z-index: -1;
}

.btn-cnc-quote:hover::before {
  left: 0;
}

.text-small {
  font-size: 0.75rem;
  color: #17202a;
  margin-top: -3px;
  font-weight: 600;
}
.qa-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
  line-height: 1.6;
}

/* Typography */
.section-title {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 500;
}

.column-title, .block-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 500;
}

.intro-text {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 60px;
  max-width: 1000px;
}

.block-desc {
  font-size: 1.05rem;
  color: #555;
  margin-bottom: 15px;
}

/* Grid Layouts */
.grid-2-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;
}

.feature-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 80px;
}

/* Allows text on left, image on right */
.feature-block.reverse .text-content {
  order: -1;
}

/* Lists */
.bullet-list {
  list-style-type: disc;
  padding-left: 20px;
  color: #555;
}

.bullet-list li {
  margin-bottom: 12px;
  font-size: 1.05rem;
}

.arrow-list {
  list-style-type: none;
  padding-left: 0;
}

.arrow-list li {
  position: relative;
  padding-left: 20px;
  margin-bottom: 10px;
  font-size: 1.05rem;
  color: #555;
}

.arrow-list li::before {
  content: '▶';
  position: absolute;
  left: 0;
  color: #16bc9c; /* Blue arrow color */
  font-size: 0.8rem;
  top: 4px;
}

/* Images & Carousels */
.image-wrapper {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  text-align: center;
}

.image-wrapper img {
  width: 100%;
  height: auto;
  border-radius: 12px;
  display: block;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.carousel-dots {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  display: inline-block;
  cursor: pointer;
  transition: background-color 0.3s;
}

.dot:hover {
  background-color: #888;
}

.dot.active {
  background-color: #333;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .grid-2-col, .feature-block {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .feature-block.reverse .text-content {
    order: 0; /* Reset order on mobile */
  }
}
@media (max-width: 1024px) {
  /* Hero Banner 自适应 */
  .fct-hero-im .container {
    padding: 0 40px; 
  }
  .content-block {
    width: 100%; 
  }
  .hero-title {
    white-space: normal; 
  }
  .hero-mask {
    background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0.95) 60%, rgba(255, 255, 255, 0) 100%);
  }
}
</style>